import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Button } from '@openx/components-core/lib/Button/Button';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { theme } from '@openx/components-core/lib/theme/theme';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext';
import { useShowMoreSeatNames } from '../hooks/useShowMoreSeatNames';

export interface DspSeatIdsReadOnlyProps {
  dspId: string;
  seatIds: string[];
}

export const DspSeatIdsReadOnly = ({ dspId, seatIds }: DspSeatIdsReadOnlyProps) => {
  const { t } = useTranslation();

  const { useDspSeatsFetch } = useOrganizationContext();
  const { mapById: seatOptions, loading: seatLoading } = useDspSeatsFetch(dspId);

  const seatNameItems = useMemo(() => seatIds.map(id => seatOptions[id]?.name || id), [seatIds, seatOptions]);

  const isEmpty = seatNameItems.length === 0;

  const { expanded, hasMoreThanVisible, hiddenCount, displayedText, handleSetExpanded } = useShowMoreSeatNames({
    items: seatNameItems,
  });

  if (seatLoading) {
    return <Loader />;
  }

  return (
    <FieldDetails
      title={t('DSP Seats')}
      value={
        isEmpty ? (
          t('N / A')
        ) : (
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Box>{displayedText}</Box>

            {!expanded && hasMoreThanVisible && (
              <Box data-test="dsp-seats-hidden-count" sx={{ color: 'text.secondary' }}>
                {t(`and ${hiddenCount} more`)}
              </Box>
            )}

            {hasMoreThanVisible && (
              <Button
                data-test="dsp-seats-toggle-button"
                variant="text"
                color="primary"
                onClick={handleSetExpanded}
                sx={{ paddingLeft: 0 }}
              >
                {expanded ? t('Hide') : t('Show more')}
              </Button>
            )}
          </Stack>
        )
      }
      customStyles={{
        box: { borderLeft: `2px solid ${theme.palette.background.boxDarkest}`, paddingLeft: theme.spacing(2) },
      }}
    />
  );
};
